import { saveAs } from 'file-saver';
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  Packer,
  AlignmentType,
  WidthType,
  BorderStyle,
} from 'docx';

import moment from 'moment';

// Логотипы (раскомментируй, если нужно)
// import moLogo from '../../../../../assets/images/pdfLogos/MO.png';
// import lmsLogo from '../../../../../assets/images/pdfLogos/LMS.png';
// import gukLogo from '../../../../../assets/images/pdfLogos/GUK.png';

// ─── Утилиты ──────────────────────────────────────────────────────────────────

const createTextParagraph = (text, options = {}) => {
  const {
    bold = false,
    size = 12,
    alignment = AlignmentType.LEFT,
    spacingAfter = 200,
    font = 'Times New Roman',
  } = options;

  return new Paragraph({
    children: [
      new TextRun({
        text: text || "",
        bold,
        size: size * 2,
        font: { name: font },
      }),
    ],
    alignment,
    spacing: { after: spacingAfter },
  });
};

const createCenteredParagraph = (text, bold = true, size = 11) => {
  return createTextParagraph(text, {
    bold,
    size,
    alignment: AlignmentType.CENTER,
  });
};

// ─── Конвертация HTML-таблицы в DOCX-таблицу ──────────────────────────────────

const convertHtmlTableToDocxTable = (tableId) => {
  const tableElement = document.getElementById(tableId);
  if (!tableElement) {
    console.error(`Таблица с id="${tableId}" не найдена в DOM`);
    return null;
  }

  const rows = [];
  const tableRows = Array.from(tableElement.querySelectorAll('tr'));

  tableRows.forEach((tr, rowIndex) => {
    const cells = [];
    const tds = Array.from(tr.querySelectorAll('td, th'));

    tds.forEach((td) => {
      const text = td.textContent?.trim() || "";
      const colspan = parseInt(td.getAttribute('colspan') || '1', 10);
      const rowspan = parseInt(td.getAttribute('rowspan') || '1', 10);

      const cell = new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: text,
                size: 16, // 8pt → half-points
                font: { name: "Times New Roman" },
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 50 },
          }),
        ],
        columnSpan: colspan > 1 ? colspan : undefined,
        rowSpan: rowspan > 1 ? rowspan : undefined,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
          right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        },
        verticalAlign: "center",
      });

      cells.push(cell);
    });

    rows.push(new TableRow({ children: cells }));
  });

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    margins: { top: 200, bottom: 200 },
  });
};

// ─── Основная функция ─────────────────────────────────────────────────────────

const getCertificationRegisterGukReportDocx = async ({ comission, userInfo }) => {
  const { last_name, first_name, patronymic } = userInfo;
  const createrName = `${last_name || ''} ${first_name || ''} ${patronymic || ''}`;
  const now = moment();
  const dateFrom = moment(comission.date_from).format('DD.MM.YYYY');
  const dateTo = moment(comission.date_to).format('DD.MM.YYYY');

  const content = [];

  // === ЗАГОЛОВОК ===

  content.push(
    createCenteredParagraph('Реестр', true, 11),
    createCenteredParagraph('педагогических работников организаций, находящихся в ведении Министерства обороны Российской Федерации', true, 11),
    createCenteredParagraph('и осуществляющих образовательную деятельность, подавших заявления для прохождения аттестации', true, 11),
    createCenteredParagraph(`${now.format('MMMM')} ${now.year()} года`, true, 11),
    createTextParagraph(`Период: ${dateFrom} - ${dateTo}`, { size: 12, alignment: AlignmentType.LEFT, spacingAfter: 300 })
  );

  // === ТАБЛИЦА ИЗ DOM ===

  const docxTable = convertHtmlTableToDocxTable('certificationRegisterTable');
  if (docxTable) {
    content.push(docxTable);
  } else {
    content.push(
      createCenteredParagraph('[Таблица не найдена]', false, 12)
    );
  }

  // === ПОДПИСЬ ===

  content.push(
    createTextParagraph('Руководитель экспертной группы                   Близнова Н.А.', {
      size: 16,
      alignment: AlignmentType.LEFT,
      spacingAfter: 600,
    })
  );

  // === ПОДВАЛ (аналог addLogos) — без картинок, только текст ===

  content.push(
    createTextParagraph(`Кто создал: ${createrName}`, { size: 8, alignment: AlignmentType.LEFT }),
    createTextParagraph(`Дата: ${moment(comission.date_to).format('DD.MM.YYYY')}`, { size: 8, alignment: AlignmentType.LEFT }),
    createTextParagraph('Документ сформирован в системе управления обучением', { size: 8, italic: true, alignment: AlignmentType.LEFT })
  );

  // Создаём документ
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Times New Roman",
            size: 24, // 12pt
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1000,   // 1 дюйм = 1440 twips → ~0.7 дюйма
              bottom: 1000,
              left: 1140,
              right: 1140,
            },
          },
        },
        children: content,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  return blob;
};

export default getCertificationRegisterGukReportDocx;